import { STOMP_ENDPOINT } from "@safers/api";
import { API_BASE_URL } from "./config";
import { Client } from "@stomp/stompjs";
import type { IMessage } from "@stomp/stompjs";

export type ConnectionStatus = "disconnected" | "connecting" | "connected" | "error";

type StatusListener = (status: ConnectionStatus) => void;

let client: Client | null = null;
let currentStatus: ConnectionStatus = "disconnected";
const listeners = new Set<StatusListener>();

function getWebSocketUrl(): string {
  const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${wsProtocol}//${window.location.host}${API_BASE_URL}${STOMP_ENDPOINT}`;
}

function setStatus(status: ConnectionStatus) {
  currentStatus = status;
  for (const listener of listeners) {
    try {
      listener(status);
    } catch (e) {
      void e;
    }
  }
}

export const stompService = {
  connect(onStatusChange?: StatusListener) {
    if (onStatusChange) {
      listeners.add(onStatusChange);
      onStatusChange(currentStatus);
    }
    if (client?.active) return;

    const instance = new Client({
      brokerURL: getWebSocketUrl(),
      reconnectDelay: 5000,
      debug: () => {},
      onConnect: () => {
        if (client === instance) setStatus("connected");
      },
      onDisconnect: () => {
        if (client === instance) setStatus("disconnected");
      },
      onStompError: () => {
        if (client === instance) setStatus("error");
      },
      onWebSocketClose: () => {
        if (client === instance) setStatus("disconnected");
      },
    });
    client = instance;

    setStatus("connecting");
    instance.activate();
  },

  subscribe(destination: string, handler: (message: IMessage) => void): () => void {
    if (!client?.connected) {
      return () => {};
    }

    const subscription = client.subscribe(destination, handler);
    return () => subscription.unsubscribe();
  },

  disconnect(onStatusChange?: StatusListener) {
    if (onStatusChange) listeners.delete(onStatusChange);
    if (listeners.size > 0) return;
    if (client?.active) {
      client.deactivate();
    }
    client = null;
    currentStatus = "disconnected";
  },

  get connected() {
    return client?.connected ?? false;
  },
};
